import { useMemo } from 'react'
import { useFormState } from 'react-hook-form'

import { useFormWithZod } from '#/shared/lib/form'
import { EMPTY_SHIPMENT_DEFAULTS, useShipmentSchema } from './shipment-schema'
import type { ShipmentFormData } from './shipment-schema'

interface UseShipmentFormParams {
  defaultValues?: Partial<ShipmentFormData>
  onSubmit?: (data: ShipmentFormData) => void | Promise<void>
  requireDates?: boolean
}

export function useShipmentForm({
  defaultValues,
  onSubmit,
  requireDates = false,
}: UseShipmentFormParams = {}) {
  const schema = useShipmentSchema(requireDates)
  const values = useMemo<ShipmentFormData>(
    () => ({ ...EMPTY_SHIPMENT_DEFAULTS, ...defaultValues }),
    [defaultValues],
  )
  const form = useFormWithZod(schema, {
    defaultValues: values,
    values,
    resetOptions: { keepDirtyValues: true },
    mode: 'onChange',
    reValidateMode: 'onChange',
  })
  const { errors, dirtyFields } = useFormState({ control: form.control })
  const isDirty = Object.keys(dirtyFields).length > 0

  return {
    control: form.control,
    register: form.register,
    getValues: form.getValues,
    trigger: form.trigger,
    errors,
    isSubmitting: form.isSubmitting,
    isDirty,
    handleSubmit: onSubmit ? form.handleSubmit(onSubmit) : undefined,
    reset: form.reset,
  }
}
